// The addressable screens: the deal screens (TX-US-0004, "Routes"), the delivery tracker
// (TX-US-0010), the counterparty register (TX-US-0016), Economics (TX-US-0025) and the commercial
// hub (TX-US-0028).
//
// Creating and editing are routes rather than overlays on the register: an edit form is a place
// you can be sent a link to, and keeping the read view and the edit view apart gives the
// optimistic-concurrency token one save path and one conflict point.

import { createBrowserRouter, Navigate } from 'react-router';

import App, { AdminGate, Home } from './App.jsx';
import CounterpartyDetail from './counterparties/CounterpartyDetail.jsx';
import CounterpartyForm from './counterparties/CounterpartyForm.jsx';
import CounterpartyRegister from './counterparties/CounterpartyRegister.jsx';
import DealDetail from './deals/DealDetail.jsx';
import DealEditRoute from './deals/DealEditRoute.jsx';
import DealForm from './deals/DealForm.jsx';
import DealRegister from './deals/DealRegister.jsx';
import DeliveryForm from './deliveries/DeliveryForm.jsx';
import DeliveryTracker from './deliveries/DeliveryTracker.jsx';
import EconomicsHome from './economics/EconomicsHome.jsx';
import FleetCalculator from './economics/fleet/FleetCalculator.jsx';
import HubHome from './hub/HubHome.jsx';
import ScenarioScreen from './economics/ScenarioScreen.jsx';
import Settings from './settings/Settings.jsx';
import UsersPage from './settings/UsersPage.jsx';

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      // Home is the first enabled feature (App.jsx), not a fixed screen.
      { index: true, element: <Home /> },
      { path: 'deals', element: <DealRegister /> },
      // No pre-deal id: none has been allocated. This route exists only until the first save.
      { path: 'deals/new', element: <DealForm mode="create" /> },
      { path: 'deals/:preDealId', element: <DealDetail /> },
      // With the deal flow on, a deal is edited on its page, and this leads there.
      { path: 'deals/:preDealId/edit', element: <DealEditRoute /> },
      // The tracker opens on the supplier leg; each leg is its own address (AC1).
      { path: 'deliveries', element: <Navigate to="/deliveries/supplier" replace /> },
      { path: 'deliveries/:leg', element: <DeliveryTracker /> },
      { path: 'deliveries/:leg/new', element: <DeliveryForm mode="create" /> },
      { path: 'deliveries/:leg/:deliveryId/edit', element: <DeliveryForm mode="edit" /> },
      // The counterparty register (TX-US-0016).
      { path: 'counterparties', element: <CounterpartyRegister /> },
      { path: 'counterparties/new', element: <CounterpartyForm mode="create" /> },
      { path: 'counterparties/:counterpartyId', element: <CounterpartyDetail /> },
      { path: 'counterparties/:counterpartyId/edit', element: <CounterpartyForm mode="edit" /> },
      // Economics (TX-US-0025): a model opens on its default scenario; each scenario is an address.
      { path: 'economics', element: <EconomicsHome /> },
      // The fleet requirement calculator (TX-US-0030): one shared list, so one address.
      { path: 'economics/fleet', element: <FleetCalculator /> },
      { path: 'economics/:modelId', element: <Navigate to="scenario/default" replace /> },
      { path: 'economics/:modelId/scenario/:scenarioId', element: <ScenarioScreen /> },
      // The commercial hub (TX-US-0028): a BD's own updates, one screen.
      { path: 'hub', element: <HubHome /> },
      // Settings (TX-US-0021): admin-only.
      { path: 'settings', element: <Navigate to="/settings/users" replace /> },
      {
        path: 'settings',
        element: <AdminGate><Settings /></AdminGate>,
        children: [
          { path: 'users', element: <UsersPage /> },
        ],
      },
    ],
  },
]);
